import React, { createContext, useCallback, useContext, useEffect, useRef } from "react";

interface ConfettiContextValue {
  /** Fire a celebratory confetti burst over the whole viewport. */
  celebrate: () => void;
}

const ConfettiContext = createContext<ConfettiContextValue | null>(null);

const COLORS = ["#FFC700", "#FF3D6E", "#00C2FF", "#26D07C", "#A45BFF", "#FF8A00"];
const PARTICLES_PER_BURST = 160;
const GRAVITY = 900; // px/s^2
const DRAG = 0.86; // velocity retained per second

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vrot: number;
  size: number;
  color: string;
  /** Seconds of life remaining; particle fades as this approaches 0. */
  life: number;
  ttl: number;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function ConfettiProvider({ children }: { children: React.ReactNode }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);

  // Keep the backing store sized to the viewport (accounting for DPR) so
  // confetti stays crisp and covers the full screen on resize/orientation.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const tick = useCallback((ts: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) {
      rafRef.current = null;
      lastTsRef.current = null;
      return;
    }

    const last = lastTsRef.current ?? ts;
    // Clamp dt so a backgrounded tab doesn't teleport particles on return.
    const dt = Math.min((ts - last) / 1000, 0.05);
    lastTsRef.current = ts;

    const width = window.innerWidth;
    const height = window.innerHeight;
    ctx.clearRect(0, 0, width, height);

    const dragFactor = Math.pow(DRAG, dt);
    const particles = particlesRef.current;
    const alive: Particle[] = [];

    for (const p of particles) {
      p.life -= dt;
      if (p.life <= 0 || p.y > height + 40) continue;

      p.vy += GRAVITY * dt;
      p.vx *= dragFactor;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vrot * dt;

      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life / p.ttl));
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      ctx.restore();

      alive.push(p);
    }

    particlesRef.current = alive;

    if (alive.length > 0) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      rafRef.current = null;
      lastTsRef.current = null;
      ctx.clearRect(0, 0, width, height);
    }
  }, []);

  const celebrate = useCallback(() => {
    if (typeof window === "undefined") return;
    if (prefersReducedMotion()) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = window.innerWidth;
    const ttl = 2.6;
    for (let i = 0; i < PARTICLES_PER_BURST; i++) {
      // Launch upward-and-outward from two lower corners for a "pop" feel,
      // varied by index so repeat bursts look different without randomness.
      const fromLeft = i % 2 === 0;
      const spread = (i / PARTICLES_PER_BURST) * Math.PI; // 0..PI
      const angle = fromLeft ? spread : Math.PI - spread; // mirror to the right
      const speed = 600 + (i % 7) * 90;
      particlesRef.current.push({
        x: fromLeft ? width * 0.12 : width * 0.88,
        y: window.innerHeight + 10,
        vx: Math.cos(angle) * speed,
        vy: -Math.abs(Math.sin(angle) * speed) - 300,
        rot: spread,
        vrot: (i % 2 === 0 ? 1 : -1) * (4 + (i % 5)),
        size: 8 + (i % 4) * 3,
        color: COLORS[i % COLORS.length] ?? "#FFC700",
        life: ttl,
        ttl,
      });
    }

    if (rafRef.current === null) {
      lastTsRef.current = null;
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [tick]);

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <ConfettiContext.Provider value={{ celebrate }}>
      {children}
      <canvas ref={canvasRef} className="confetti-canvas" aria-hidden="true" />
    </ConfettiContext.Provider>
  );
}

export function useConfetti(): ConfettiContextValue {
  const ctx = useContext(ConfettiContext);
  if (!ctx) {
    throw new Error("useConfetti must be used within ConfettiProvider");
  }
  return ctx;
}
